


import {ReactElement,ChangeEvent, useState, FormEvent, ReactNode} from 'react'
import {Trans, WithNamespaces, withNamespaces} from 'react-i18next'
import {Button, Grid, Typography} from '@material-ui/core'
import PageTitle from '../../../../../../Common/PageTitle'
import PageBody from '../../../../../../Common/PageBody'
import messages from '../../../../../../../assets/messages'
import Password from '../../../../../../Common/Password'
import NotificationBar from '../../../../../../Common/NotificationBar'
import {ApiError, useUpdateProvider} from '../../hooks/useUpdateProvider'
import useField from './hooks/useField'
import {useStyles} from './styles'

type FormValues = {
  password: string
  passwordConfirmation: string
}

type StartProvidingFormBaseProps = {
  accountId: number
  onProviderEnabled: () => Promise<void>
} & WithNamespaces

const StartProvidingFormBase = (props: StartProvidingFormBaseProps): ReactElement => {
  const {t, accountId, onProviderEnabled} = props
  const classes = useStyles()

  const passwordField = useField('')
  const passwordConfirmationField = useField('')

  const formValues = {
    password: passwordField.value,
    passwordConfirmation: passwordConfirmationField.value
  }

  const passwordError = validatePassword(formValues)
  const passwordConfirmationError = validatePasswordConfirmation(formValues)

  const hasValidationError = Boolean(passwordError || passwordConfirmationError)

  const [submitErrorMessage, setSubmitErrorMessage] = useState<string | null>(null)

  const handleUpdateSuccess = async (): Promise<void> => {
    setSubmitErrorMessage(null)
    await onProviderEnabled()
  }

  const handleUpdateError = (_body, e): void => {
    setSubmitErrorMessage(getErrorMessage(e, t))
  }

  const {updateProvider, loading} = useUpdateProvider(accountId, {onError: handleUpdateError, onSuccess: handleUpdateSuccess})

  const handlePasswordChange = (e: ChangeEvent<HTMLInputElement>) => {
    passwordField.changeValue(e.target.value.trim())
  }

  const handlePasswordConfirmationChange = (e: ChangeEvent<HTMLInputElement>) => {
    passwordConfirmationField.changeValue(e.target.value.trim())
  }

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault()
    if (!hasValidationError) {
      await updateProvider({isProviding: true, password: passwordField.value})
    }
  }

  return (
    <>
      <PageTitle title={t(messages.setupPerformanceFeeAccountTitle.i18nKey, messages.setupPerformanceFeeAccountTitle.defaults)} />
      <PageBody>
        <form onSubmit={handleSubmit}>
          <Grid container>
            <Grid item sm={12} md={7} className={classes.container}>
              <Typography variant="caption" className={classes.text}>
                <Trans {...messages.setupPerformanceFeeAccountText1} components={[<strong> </strong>]} />
              </Typography>
              <br/>
              <Typography variant="caption" className={classes.text}>
                <Trans {...messages.setupPerformanceFeeAccountText2} />
              </Typography>
              <Password
                classes={{formControl: classes.input}}
                value={passwordField.value}
                errorText={passwordError}
                fullWidth
                readOnly={loading}
                error={Boolean(passwordError && passwordField.isTouched)}
                showPassword={passwordField.isVisible}
                onChange={handlePasswordChange}
                onClickShow={passwordField.toggleVisibility}
              />
              <Password
                classes={{formControl: classes.input}}
                value={passwordConfirmationField.value}
                errorText={passwordConfirmationError}
                label='passwordConfirmation'
                fullWidth
                readOnly={loading}
                error={Boolean(passwordConfirmationError && passwordConfirmationField.isTouched)}
                showPassword={passwordConfirmationField.isVisible}
                onChange={handlePasswordConfirmationChange}
                onClickShow={passwordConfirmationField.toggleVisibility}
              />
              {submitErrorMessage != null ? <NotificationBar status="error">{submitErrorMessage}</NotificationBar> : null}
              <Button
                className={classes.button}
                variant="contained"
                size="large"
                color="primary"
                type="submit"
                disabled={hasValidationError || loading}
              >
                <Trans {...messages.createAccount} />
              </Button>
            </Grid>
          </Grid>
        </form>
      </PageBody>
    </>
  )
}

export const StartProvidingForm = withNamespaces()(StartProvidingFormBase)

function validatePassword(formValues: FormValues): ReactNode {
  const {password} = formValues

  if (password.length === 0) return (<Trans {...messages.requiredField} />)
}

function validatePasswordConfirmation(formValues: FormValues): ReactNode {
  const {password, passwordConfirmation} = formValues

  const areDiffierent = password.length !== 0 && passwordConfirmation.length !== 0 && password !== passwordConfirmation

  if (passwordConfirmation.length === 0) return <Trans {...messages.requiredField} />
  if (areDiffierent) return (<Trans {...messages.passwordsNotSame} />)
}

function getErrorMessage(e: unknown, t: WithNamespaces['t']): string {
  if (e instanceof ApiError && e.payload?.actionErrorCode === 'invalidPassword') {
    return t(messages.passwordMismatch.i18nKey, messages.passwordMismatch.defaults)
  }

  return t(messages.somethingWrongError.i18nKey, messages.somethingWrongError.defaults)
}
